import { Button, Menu, MenuItem } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { SortPropertyDescriptor } from 'framework/components/lists/SortPropertyDescriptor';
import { useAnchorElement } from 'framework/hooks/useAnchorElement';
import { createType } from 'framework/utils/createType';
import { ISortParams, ITender } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React from 'react';
import { RecordContext } from 'shared/records/RecordContext';

interface IProps {
	sortParams: ISortParams<ITender>;
	onFilter: (sp: ISortParams<ITender>) => void;
}

const TenderOrderableProperties = createType('created', 'reference', 'priceInVat');
type TenderOrderableProperty = typeof TenderOrderableProperties[number];

const createOrderablePropertyRecord = (strings: IStrings): Record<TenderOrderableProperty, string> => {
	return {
		priceInVat: strings.priceInVat,
		created: strings.dateCreated,
		reference: strings.reference,
	};
};

const OrderablePropertyToSortPropertyDescriptorRecord: Record<TenderOrderableProperty, SortPropertyDescriptor> = {
	priceInVat: 'price',
	created: 'date',
	reference: 'string',
};

export const TendersListOrderBySelect = ({ sortParams, onFilter }: IProps) => {
	const strings = useLocalization();
	const [anchor, open, close] = useAnchorElement();
	const OrderablePropertyRecord = React.useMemo(() => createOrderablePropertyRecord(strings), [strings]);
	const { ascSortPropertyRecord, descSortPropertyRecord } = React.useContext(RecordContext);

	const title = React.useMemo(() => {
		if (TenderOrderableProperties.indexOf(sortParams.property as any) > -1) {
			const prop = sortParams.property as TenderOrderableProperty;
			return `${OrderablePropertyRecord[prop]} (${
				sortParams.direction === 'asc'
					? ascSortPropertyRecord[OrderablePropertyToSortPropertyDescriptorRecord[prop]]
					: descSortPropertyRecord[OrderablePropertyToSortPropertyDescriptorRecord[prop]]
			})`;
		} else {
			return strings.unknown;
		}
		// eslint-disable-next-line
	}, [strings, sortParams]);

	const onSelect = (property: TenderOrderableProperty & keyof ITender, direction: 'asc' | 'desc') => {
		onFilter({ property: property, direction: direction });
		close();
	};

	return (
		<>
			<Button
				size='small'
				endIcon={<ArrowDropDownIcon />}
				onClick={open}>
				{strings.sortOnWhat(title)}
			</Button>
			<Menu
				anchorEl={anchor}
				open={Boolean(anchor)}
				onClose={close}
				keepMounted>
				{TenderOrderableProperties.map(prop => (
					<div key={prop}>
						<MenuItem
							key={`asc-${prop}`}
							onClick={() => onSelect(prop, 'asc')}>
							{`${strings.sortOnWhat(OrderablePropertyRecord[prop])} (${ascSortPropertyRecord[OrderablePropertyToSortPropertyDescriptorRecord[prop]]})`}
						</MenuItem>
						<MenuItem
							key={`desc-${prop}`}
							onClick={() => onSelect(prop, 'desc')}>
							{`${strings.sortOnWhat(OrderablePropertyRecord[prop])} (${descSortPropertyRecord[OrderablePropertyToSortPropertyDescriptorRecord[prop]]})`}
						</MenuItem>
					</div>
				))}
			</Menu>
		</>
	);
};
